import CreateAxiosInstance, { reportApiErrorGlobally } from './axiosConfig';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

// ---------------------------------------------------------------------------
// Contractor status check
// ---------------------------------------------------------------------------

export async function getEvalAccess() {
  return await APIClient.get('/eval/access');
}

// ---------------------------------------------------------------------------
// Packets (review queue — DB-backed)
// ---------------------------------------------------------------------------

/**
 * `quiet` suppresses the global error modal. Used by the ?packet= deep-link probe,
 * where a 403/404 is an expected answer ("that packet isn't reviewable by you")
 * that the caller degrades from, not something to interrupt the reviewer over.
 */
export async function getPacketDetail(packetId, { quiet = false } = {}) {
  return await APIClient.get(
    `/admin/eval/packets/${packetId}`,
    quiet ? { customErrorHandler: () => {} } : undefined
  );
}

export async function getPacketSegments(packets) {
  // packets: [{packet_id}]
  return await APIClient.post('/admin/eval/packets/segments', { packets });
}

export async function browsePackets({
  limit = 50,
  offset,
  start_date,
  end_date,
  language,
  segment,
  subject,
  grade_level,
  course,
  role,
  contractor_id,
} = {}) {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (offset) params.append('offset', offset);
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  if (language) params.append('language', language);
  if (segment) params.append('segment', segment);
  if (subject) params.append('subject', subject);
  if (grade_level) params.append('grade_level', grade_level);
  if (course) params.append('course', course);
  if (role) params.append('role', role);
  if (contractor_id) params.append('contractor_id', contractor_id);
  return await APIClient.get(`/admin/eval/packets/browse?${params.toString()}`);
}

/**
 * Distinct creator subject / grade level / course / role values present in the
 * eval packet pool, with the number of packets each returns on its own.
 * Scoped by the same date + language window as the browse it populates.
 */
export async function getPacketFilterOptions({ start_date, end_date, language } = {}) {
  const params = new URLSearchParams();
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  if (language) params.append('language', language);
  const qs = params.toString();
  // Fire-and-forget: the caller degrades to no filter options on its own, so a
  // failure here shouldn't raise the global error modal over the dashboard.
  return await APIClient.get(`/admin/eval/packets/filter-options${qs ? `?${qs}` : ''}`, {
    customErrorHandler: () => {},
  });
}

// ---------------------------------------------------------------------------
// Comments (shared endpoints — used by both admins and contractors)
// ---------------------------------------------------------------------------

export async function createComment({
  packet_id,
  block_unique_id,
  comment_text,
  selected_text,
  highlight_rects,
  tags,
}) {
  return await APIClient.post('/eval/comments', {
    packet_id,
    block_unique_id,
    comment_text,
    selected_text,
    highlight_rects,
    tags,
  });
}

export async function listComments(packetId) {
  const params = new URLSearchParams({ packet_id: packetId });
  return await APIClient.get(`/eval/comments?${params.toString()}`);
}

export async function editComment(commentId, { comment_text, tags }) {
  return await APIClient.patch(`/eval/comments/${commentId}`, { comment_text, tags });
}

export async function deleteComment(commentId) {
  return await APIClient.delete(`/eval/comments/${commentId}`);
}

// ---------------------------------------------------------------------------
// Reviews (shared endpoints — used by both admins and contractors)
// ---------------------------------------------------------------------------

export async function markReviewed(packetId) {
  return await APIClient.post('/eval/reviews', { packet_id: packetId });
}

/**
 * A 404 here is an expected answer, not a fault: mark/unmark act on your OWN review,
 * while the reviewed-state indicator reports a packet as reviewed when anyone has
 * completed it. So an admin can see the toggle lit for a packet they have no review
 * of, and clicking it has nothing to undo. The caller reports that inline; the global
 * error modal would be the wrong weight for it. Other statuses still surface normally.
 */
export async function unmarkReviewed(packetId) {
  return await APIClient.delete(`/eval/reviews/${packetId}`, {
    customErrorHandler: (error) => {
      if (error.response?.status !== 404) reportApiErrorGlobally(error);
    },
  });
}

export async function getReviewStatus(packetIds) {
  const params = new URLSearchParams();
  params.append('packet_ids', packetIds.join(','));
  return await APIClient.get(`/eval/reviews/status?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Contractors
// ---------------------------------------------------------------------------

export async function listContractors() {
  return await APIClient.get('/admin/eval/contractors');
}

export async function createContractor({ email, specialties }) {
  return await APIClient.post('/admin/eval/contractors', {
    email,
    specialties,
  });
}

export async function updateContractor(contractorId, data) {
  return await APIClient.put(`/admin/eval/contractors/${contractorId}`, data);
}

export async function deleteContractor(contractorId) {
  return await APIClient.delete(`/admin/eval/contractors/${contractorId}`);
}

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

export async function createAssignments({ contractor_id, packet_ids, segments }) {
  return await APIClient.post('/admin/eval/assignments', {
    contractor_id,
    packet_ids,
    segments,
  });
}

export async function listAssignments({ contractor_id, status, segment } = {}) {
  const params = new URLSearchParams();
  if (contractor_id) params.append('contractor_id', contractor_id);
  if (status) params.append('status', status);
  if (segment) params.append('segment', segment);
  return await APIClient.get(`/admin/eval/assignments?${params.toString()}`);
}

export async function deleteAssignment(assignmentId) {
  return await APIClient.delete(`/admin/eval/assignments/${assignmentId}`);
}

/**
 * Bulk-delete a contractor's assignments. `status` narrows which rows go —
 * omitting it deletes completed reviews too, which also wipes the contractor's
 * completed_count and progress record, since those are derived from these rows.
 */
export async function deleteAllAssignments({ contractor_id, status }) {
  const params = new URLSearchParams();
  params.append('contractor_id', contractor_id);
  if (status) params.append('status', status);
  return await APIClient.delete(`/admin/eval/assignments?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Contractor self-service (non-admin endpoints)
// ---------------------------------------------------------------------------

export async function browseMyPackets({ limit = 10, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (offset) params.append('offset', offset);
  return await APIClient.get(`/eval/my-packets/browse?${params.toString()}`);
}

export async function getMyAssignments() {
  return await APIClient.get('/eval/my-assignments');
}

export async function contractorGetPacketDetail(packetId, { quiet = false } = {}) {
  return await APIClient.get(
    `/eval/packets/${packetId}`,
    quiet ? { customErrorHandler: () => {} } : undefined
  );
}

// ---------------------------------------------------------------------------
// Comments export (admin only)
// ---------------------------------------------------------------------------

export async function exportComments({ start_date, end_date, language, segment } = {}) {
  const params = new URLSearchParams();
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  if (language) params.append('language', language);
  if (segment) params.append('segment', segment);
  const qs = params.toString();
  return await APIClient.get(`/admin/eval/comments/export${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// Utilities (shared endpoint — used by both admins and contractors)
// ---------------------------------------------------------------------------

export async function presignS3Url(url) {
  return await APIClient.get(`/eval/presign?url=${encodeURIComponent(url)}`);
}

// ---------------------------------------------------------------------------
// Pairwise comparisons (admin only). Started from the workshop in debug mode —
// see createResourceStream's `compare` param.
// ---------------------------------------------------------------------------

export async function getServingGeneratorConfig() {
  return await APIClient.get('/admin/eval/generator-config/serving');
}

export async function listExperiments() {
  return await APIClient.get('/admin/eval/experiments');
}

export async function createExperiment({ name, config_patch, flag_patch }) {
  return await APIClient.post('/admin/eval/experiments', { name, config_patch, flag_patch });
}

export async function archiveExperiment(experimentId) {
  return await APIClient.post(`/admin/eval/experiments/${experimentId}/archive`);
}

export async function listComparisons() {
  return await APIClient.get('/admin/eval/comparisons');
}

export async function getComparison(comparisonId) {
  return await APIClient.get(`/admin/eval/comparisons/${comparisonId}`);
}

export async function getComparisonVersion(comparisonId, version) {
  return await APIClient.get(`/admin/eval/comparisons/${comparisonId}/versions/${version}`);
}

export async function voteOnComparison(comparisonId, { winner, rationale }) {
  return await APIClient.post(`/admin/eval/comparisons/${comparisonId}/vote`, {
    winner,
    rationale,
  });
}
